import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, CalendarCheck, FileQuestion, ClipboardList,
  LogOut, GraduationCap, Menu, X, Table2, Settings as SettingsIcon, KeyRound, Loader2,
} from "lucide-react";
import { useState } from "react";

const adminNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/karyawan", label: "Karyawan", icon: Users, testid: "nav-employees" },
  { to: "/kehadiran", label: "Kehadiran", icon: CalendarCheck, testid: "nav-attendance" },
  { to: "/kuis", label: "Kuis", icon: FileQuestion, testid: "nav-quiz" },
  { to: "/penilaian", label: "Rekap Penilaian", icon: ClipboardList, testid: "nav-assessment" },
  { to: "/gradebook", label: "Gradebook", icon: Table2, testid: "nav-gradebook" },
];

const employeeNav = [
  { to: "/portal", label: "Beranda", icon: LayoutDashboard, testid: "nav-portal" },
  { to: "/kehadiran", label: "Absen Kehadiran", icon: CalendarCheck, testid: "nav-attendance" },
  { to: "/kuis", label: "Ikuti Kuis", icon: FileQuestion, testid: "nav-quiz" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    setSavingPw(true);
    try {
      await api.put("/auth/password", { old_password: oldPw, new_password: newPw });
      toast.success("Password berhasil diubah");
      setPwOpen(false); setOldPw(""); setNewPw("");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSavingPw(false);
    }
  };
  const baseNav = user?.role === "admin" ? adminNav : employeeNav;
  const nav = user?.role === "admin" && user?.is_super
    ? [...baseNav, { to: "/pengaturan", label: "Pengaturan", icon: SettingsIcon, testid: "nav-settings" }]
    : baseNav;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SideContent = () => (
    <>
      <div className="flex items-center gap-3 px-6 h-20 border-b border-slate-800">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-orange-500">
          <GraduationCap className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="font-heading font-extrabold text-white leading-tight text-sm">Web Monitoring Pengembangan Karyawan</p>
          <p className="text-[11px] text-slate-400">Monitoring Pengembangan</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 pb-6">
        <div className="rounded-xl bg-slate-800/60 p-4 mb-3">
          <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
          <p className="text-xs text-slate-400 capitalize">{user?.role === "admin" ? (user?.is_super ? "Administrator Utama" : "Administrator (Hanya Lihat)") : "Karyawan"}</p>
        </div>
        <button
          data-testid="change-own-password-button"
          onClick={() => { setOpen(false); setPwOpen(true); }}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 mb-1 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
        >
          <KeyRound className="h-5 w-5" />
          Ubah Password
        </button>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-300 hover:bg-rose-600/90 hover:text-white transition-colors"
        >
          <LogOut className="h-5 w-5" />
          Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-slate-900">
        <SideContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative flex w-64 flex-col bg-slate-900">
            <SideContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-slate-900 px-4 h-16">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-blue-400" />
            <span className="font-heading font-bold text-white text-sm">Web Monitoring Pengembangan Karyawan</span>
          </div>
          <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} className="text-white">
            {open ? <X /> : <Menu />}
          </button>
        </header>
        <main className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto">{children}</main>
      </div>

      {pwOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !savingPw && setPwOpen(false)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" data-testid="change-password-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading text-lg font-bold text-slate-900">Ubah Password</h3>
              <button onClick={() => !savingPw && setPwOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <p className="mb-4 text-sm text-slate-500">Masukkan password lama dan password baru Anda.</p>
            <form onSubmit={submitPw} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password Lama</label>
                <input data-testid="old-password-input" type="password" required value={oldPw} onChange={(e) => setOldPw(e.target.value)} placeholder="••••••••" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password Baru</label>
                <input data-testid="new-password-input" type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="••••••••" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition" />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setPwOpen(false)} disabled={savingPw} className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition">Batal</button>
                <button data-testid="submit-change-password" type="submit" disabled={savingPw} className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition">
                  {savingPw && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
